import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  alpha,
  useTheme,
  CircularProgress,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { ArrowBack, TrendingUp, Group, Autorenew, AccountBalanceWallet } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../services/axiosInstance";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import AdminSideNav from "../../../adminComponents/sidenav";
import AdminHeader from "../../../adminComponents/header";

const RevenueAnalytics = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    monthlyRevenue: [],
    managerRevenue: [],
    renewalSplit: [],
    currencySummary: [],
  });
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("INR");

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(apiEndPointsConfig.getRevenueAnalytics, {
        params: { startDate, endDate, currency: selectedCurrency !== "All" ? selectedCurrency : undefined },
      });
      setData(response.data.data);
    } catch (error) {
      console.error("Error fetching revenue analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [startDate, endDate, selectedCurrency]);

  const COLORS = [theme.palette.primary.main, theme.palette.secondary.main, "#00C49F", "#FFBB28", "#FF8042"];
  const STATUS_COLORS = {
    "New Client": theme.palette.primary.main,
    "Renewal": theme.palette.success.main,
  };

  const formatCurrency = (value, curr = selectedCurrency) => {
    const currencyCode = curr === "All" ? "INR" : curr;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currencyCode,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper
          sx={{
            p: 2,
            bgcolor: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(4px)",
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[3],
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="body2" sx={{ color: entry.color, fontWeight: 500 }}>
              {entry.name}: {formatCurrency(entry.value)}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  const currentSummary = data.currencySummary.find(s => s._id === selectedCurrency) || 
                        (selectedCurrency === "All" ? { totalAmount: 0, newClientRevenue: 0, renewalRevenue: 0 } : null);

  const displaySummary = selectedCurrency === "All" ? 
    data.currencySummary.reduce((acc, curr) => ({
      totalAmount: acc.totalAmount + curr.totalAmount,
      newClientRevenue: acc.newClientRevenue + curr.newClientRevenue,
      renewalRevenue: acc.renewalRevenue + curr.renewalRevenue,
    }), { totalAmount: 0, newClientRevenue: 0, renewalRevenue: 0 }) :
    (currentSummary || { totalAmount: 0, newClientRevenue: 0, renewalRevenue: 0 });

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ ml: { xs: "0px", md: "200px" } }}>
        <AdminSideNav />
        <AdminHeader title="Revenue Analytics" />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 4 },
            bgcolor: (theme) => alpha(theme.palette.grey[50], 0.5),
            minHeight: "100vh",
          }}
        >
          {/* Header Actions */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4, flexWrap: "wrap", gap: 2 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate(-1)}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Back to Records
            </Button>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={selectedCurrency}
                  label="Currency"
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                >
                  <MenuItem value="INR">INR (₹)</MenuItem>
                  <MenuItem value="AED">AED (د.إ)</MenuItem>
                  <MenuItem value="All">All (Mixed)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                type="date"
                label="Start Date"
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
              <TextField
                type="date"
                label="End Date"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
              {(startDate || endDate) && (
                <Button onClick={() => { setStartDate(""); setEndDate(""); }}>Clear</Button>
              )}
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Summary Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 3, borderRadius: 4, display: "flex", alignItems: "center", gap: 2, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)" }}>
                    <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      <AccountBalanceWallet color="primary" />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>Total Revenue</Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {formatCurrency(displaySummary.totalAmount)}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 3, borderRadius: 4, display: "flex", alignItems: "center", gap: 2, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)" }}>
                    <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                      <TrendingUp color="success" />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>New Clients</Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {formatCurrency(displaySummary.newClientRevenue)}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 3, borderRadius: 4, display: "flex", alignItems: "center", gap: 2, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)" }}>
                    <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: alpha(theme.palette.secondary.main, 0.1) }}>
                      <Autorenew color="secondary" />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>Renewals</Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {formatCurrency(displaySummary.renewalRevenue)}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 3, borderRadius: 4, display: "flex", alignItems: "center", gap: 2, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)" }}>
                    <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                      <Group color="warning" />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>Managers</Typography>
                      <Typography variant="h6" fontWeight={700}>{data.managerRevenue.length}</Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              {/* Mixed Currency Alert if "All" is selected */}
              {selectedCurrency === "All" && (
                <Paper sx={{ p: 2, mb: 4, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.1), borderLeft: `4px solid ${theme.palette.info.main}` }}>
                  <Typography variant="body2" color="info.main" fontWeight={600}>
                    Showing combined totals for all currencies. For accurate charts, please select a specific currency.
                  </Typography>
                </Paper>
              )}

              <Grid container spacing={4}>
                {/* Monthly Revenue Trend */}
                <Grid item xs={12} lg={8}>
                  <Paper sx={{ p: 3, borderRadius: 4, height: 450, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)" }}>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>Monthly Revenue Trend ({selectedCurrency})</Typography>
                    <ResponsiveContainer width="100%" height="90%">
                      <BarChart data={data.monthlyRevenue} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
                        <XAxis dataKey="_id" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: theme.palette.text.secondary }} dy={10} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: theme.palette.text.secondary }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ paddingTop: 20 }} />
                        <Bar name="New Client" dataKey="newClientRevenue" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} barSize={25} />
                        <Bar name="Renewal" dataKey="renewalRevenue" fill={theme.palette.success.main} radius={[4, 4, 0, 0]} barSize={25} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                {/* Revenue Split Pie */}
                <Grid item xs={12} lg={4}>
                  <Paper sx={{ p: 3, borderRadius: 4, height: 450, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)" }}>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>Revenue Split</Typography>
                    <ResponsiveContainer width="100%" height="90%">
                      <PieChart>
                        <Pie
                          data={data.renewalSplit}
                          cx="50%"
                          cy="50%"
                          innerRadius={80}
                          outerRadius={120}
                          paddingAngle={5}
                          dataKey="totalAmount"
                          nameKey="_id"
                        >
                          {data.renewalSplit.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry._id] || COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                {/* Manager Performance */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, borderRadius: 4, height: 450, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)" }}>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>Revenue by Account Manager</Typography>
                    <ResponsiveContainer width="100%" height="90%">
                      <BarChart data={data.managerRevenue} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={theme.palette.divider} />
                        <XAxis type="number" axisLine={false} tickLine={false} hide />
                        <YAxis type="category" dataKey="_id" axisLine={false} tickLine={false} tick={{ fontSize: 13, fontWeight: 500 }} width={120} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="totalAmount" name="Revenue" fill={alpha(theme.palette.primary.main, 0.8)} radius={[0, 4, 4, 0]} barSize={30}>
                          {data.managerRevenue.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default RevenueAnalytics;
